import React, { useEffect, useState } from 'react';
import axios from '../../axiosConfig';
import '../../styles/inputs-Css/ActivityLog.css';
import Pagination from '../../components/common/Pagination.js';
import { getTimezoneFormattedDate } from '../../utils/dateUtils.js';
import SearchBar from '../../components/common/SearchBar.js';

function assetLabel(details = {}) {
  if (details.assetLabel) return details.assetLabel;
  const t = String(details.assetType || details.asset || '').toLowerCase();
  if (t === 'cover') return 'Cover';
  if (t === 'digital') return 'Digital PDF';
  if (t === 'print') return 'Print PDF';
  if (t === 'zip') return 'ZIP';
  if (t === 'pdf') return 'PDF';
  return details.assetType || details.asset || null;
}

function bookLine(details = {}) {
  const title = details.bookTitle || 'Untitled book';
  const id = details.bookId != null ? ` (#${details.bookId})` : '';
  const section = details.section ? ` · ${details.section}` : '';
  return `"${title}"${id}${section}`;
}

function listChanged(fields) {
  if (!Array.isArray(fields) || !fields.length) return 'details';
  return fields.join(', ');
}

const formatLogDetails = (action, detailsString) => {
  let details;
  try {
    details = typeof detailsString === 'object' && detailsString !== null
      ? detailsString
      : JSON.parse(detailsString || '{}');
  } catch (e) {
    return detailsString || 'No additional details.';
  }

  const ip = details.ipAddress || null;
  const ipSuffix = ip ? ` · IP ${ip}` : '';
  const asset = assetLabel(details);

  switch (action) {
    case 'USER_LOGIN':
      return `Signed in${details.role ? ` as ${details.role}` : ''}${ipSuffix}`;
    case 'USER_REGISTER':
      return `Registered "${details.name || 'user'}" (${details.email || 'n/a'}) as ${details.role || 'user'}${ipSuffix}`;
    case 'USER_UPDATE':
      return `Updated user "${details.targetName || details.updatedFields?.name || 'user'}" (${details.targetEmail || details.updatedFields?.email || 'n/a'})${details.passwordChanged ? ' · password changed' : ''}${ipSuffix}`;
    case 'USER_DELETE':
      return `Deleted user "${details.targetName || 'user'}" (${details.targetEmail || 'n/a'})${ipSuffix}`;
    case 'CREATE_BOOK':
      return `Added book ${bookLine(details)}${details.copiedAccessFrom ? ` · copied access from #${details.copiedAccessFrom}` : ''}${ipSuffix}`;
    case 'UPDATE_BOOK':
      return `Updated book ${bookLine(details)} · changed ${listChanged(details.changedFields)}${details.isbnCode ? ` · ISBN ${details.isbnCode}` : ''}${ipSuffix}`;
    case 'DELETE_BOOK':
      return `Deleted book ${bookLine(details)}${ipSuffix}`;
    case 'UPLOAD_COVER':
      return `Uploaded cover for ${bookLine(details)}${ipSuffix}`;
    case 'UPDATE_COVER':
      return `Updated cover for ${bookLine(details)}${ipSuffix}`;
    case 'UPLOAD_BOOK_PDF':
      return `Uploaded ${asset || 'PDF'} for ${bookLine(details)}${details.versionLabel ? ` · version ${details.versionLabel}` : ''}${details.hasZip ? ' · with ZIP' : ''}${ipSuffix}`;
    case 'UPDATE_BOOK_PDF':
      return `Updated ${asset || 'file'} for ${bookLine(details)}${details.versionLabel ? ` · version ${details.versionLabel}` : ''}${ipSuffix}`;
    case 'UPLOAD_EXTRA_RESOURCE':
      return `Uploaded extra resource for ${bookLine(details)}${ipSuffix}`;
    case 'SHARE_BOOK':
      return `Shared ${bookLine(details)} with ${details.sharedWithName || details.sharedWithEmail || `user #${details.sharedWithUserId}`} (${details.permission || 'viewer'}${details.canDownload ? ', download on' : ''})${ipSuffix}`;
    case 'UNSHARE_BOOK':
      return `Removed access to ${bookLine(details)} for ${details.removedUserName || details.removedUserEmail || `user #${details.removedUserId}`}${ipSuffix}`;
    case 'APPROVE_ACCESS':
      return `Approved access to ${bookLine(details)} for ${details.approvedUserName || details.approvedUserEmail || `user #${details.approvedUserId}`}${ipSuffix}`;
    case 'REQUEST_ACCESS':
      return `Requested access to ${bookLine(details)}${ipSuffix}`;
    case 'READ_BOOK':
      return `Opened book ${bookLine(details)}${ipSuffix}`;
    case 'DOWNLOAD_PDF':
      return `Downloaded PDF for ${bookLine(details)}${details.versionLabel ? ` · ${details.versionLabel}` : ''}${ipSuffix}`;
    case 'DOWNLOAD_ZIP':
      return `Downloaded ZIP for ${bookLine(details)}${details.versionLabel ? ` · ${details.versionLabel}` : ''}${ipSuffix}`;
    case 'DOWNLOAD_COVER':
      return `Downloaded cover for ${bookLine(details)}${ipSuffix}`;
    case 'CREATE_ASSET_SHARE_LINK':
      return `Created share link for ${asset || 'asset'} on ${bookLine(details)}${ipSuffix}`;
    case 'DISABLE_ASSET_SHARE_LINK':
      return `Disabled share link${asset ? ` (${asset})` : ''} for ${details.bookTitle ? bookLine(details) : `link #${details.linkId}`}${ipSuffix}`;
    case 'UNLOCK_ASSET_SHARE_LINK':
      return `Unlocked share link for ${asset || 'asset'} on ${bookLine(details)}${ipSuffix}`;
    case 'VIEW_ASSET_SHARE':
      return `Viewed shared ${asset || 'file'} for ${bookLine(details)}${ipSuffix}`;
    case 'DOWNLOAD_ASSET_SHARE':
      return `Downloaded shared ${asset || 'file'} for ${bookLine(details)}${ipSuffix}`;
    case 'IMPORT_BOOKS_CSV':
      return `Imported CSV · ${details.successCount || 0} created, ${details.errorCount || 0} errors (${details.rowsProcessed || 0} rows)${ipSuffix}`;
    case 'SUBMIT_REVIEW':
      return `Submitted review for ${bookLine(details)}${details.reviewSubject ? ` · "${details.reviewSubject}"` : ''}${ipSuffix}`;
    default: {
      const bits = [];
      if (details.bookTitle || details.bookId) bits.push(bookLine(details));
      if (asset) bits.push(asset);
      if (details.section && !details.bookTitle) bits.push(details.section);
      const summary = bits.length ? bits.join(' · ') : 'Action completed';
      return `${summary}${ipSuffix}`;
    }
  }
};

/** Dot/chip tone using redesign CSS vars (--mint / --peach / --primary). */
function actionTone(action) {
  const key = (action || '').toUpperCase();
  if (key === 'USER_LOGIN') return 'mint';
  if (key === 'USER_UPDATE' || key === 'UPDATE_BOOK' || key === 'UPDATE_COVER' || key === 'UPDATE_BOOK_PDF') return 'peach';
  if (key === 'SUBMIT_REVIEW') return 'primary';
  if (key === 'USER_REGISTER' || key === 'CREATE_BOOK' || key === 'CREATE_ASSET_SHARE_LINK') return 'lilac';
  if (key.includes('DOWNLOAD') || key === 'READ_BOOK' || key === 'VIEW_ASSET_SHARE') return 'sky';
  if (key === 'REQUEST_ACCESS' || key === 'DELETE_BOOK' || key === 'USER_DELETE' || key === 'DISABLE_ASSET_SHARE_LINK' || key === 'UNSHARE_BOOK') return 'rose';
  if (key.includes('UPLOAD') || key === 'SHARE_BOOK' || key === 'APPROVE_ACCESS' || key === 'UNLOCK_ASSET_SHARE_LINK' || key === 'IMPORT_BOOKS_CSV') return 'sky';
  return 'lilac';
}

function ActivityLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [searchType, setSearchType] = useState('all');
  const [filteredLogs, setFilteredLogs] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [logsPerPage] = useState(50);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('token');
        const response = await axios.get('/admin/activity-logs', {
          headers: { Authorization: `Bearer ${token}` },
        });
        setLogs(response.data);
      } catch (err) {
        console.error('Failed to fetch activity logs:', err);
        setError('Could not load activity logs. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  useEffect(() => {
    const filtered = logs.filter((log) => {
      const searchTermLower = searchTerm.toLowerCase().trim();
      const logDate = new Date(log.created_at);
      const logAction = log.action?.toLowerCase() || '';
      const detailsText = typeof log.details === 'string'
        ? log.details.toLowerCase()
        : JSON.stringify(log.details || {}).toLowerCase();
      const formattedLogDate = logDate
        .toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
        .toLowerCase();

      switch (searchType) {
        case 'user':
          return (
            log.user_name?.toLowerCase().includes(searchTermLower) ||
            log.user_email?.toLowerCase().includes(searchTermLower)
          );
        case 'action':
          return logAction.includes(searchTermLower) || detailsText.includes(searchTermLower);
        case 'date': {
          const monthNames = [
            'january', 'february', 'march', 'april', 'may', 'june',
            'july', 'august', 'september', 'october', 'november', 'december',
          ];
          const logMonth = logDate.toLocaleString('en-US', { month: 'long' }).toLowerCase();
          const logDay = logDate.getDate();
          const logYear = logDate.getFullYear();

          const fullDateMatch = searchTermLower.match(/([a-z]+)\s+(\d{1,2}),?\s*(\d{4})?/);
          if (fullDateMatch) {
            const [, month, day, year] = fullDateMatch;
            if (monthNames.includes(month)) {
              if (year) {
                return logMonth === month && logDay === parseInt(day, 10) && logYear === parseInt(year, 10);
              }
              return logMonth === month && logDay === parseInt(day, 10);
            }
          }
          if (monthNames.includes(searchTermLower)) {
            return logMonth === searchTermLower;
          }
          return formattedLogDate.includes(searchTermLower);
        }
        default:
          return (
            log.user_name?.toLowerCase().includes(searchTermLower) ||
            log.user_email?.toLowerCase().includes(searchTermLower) ||
            logAction.includes(searchTermLower) ||
            detailsText.includes(searchTermLower) ||
            formattedLogDate.includes(searchTermLower)
          );
      }
    });
    setFilteredLogs(filtered);
    setCurrentPage(1);
  }, [logs, searchTerm, searchType]);

  const indexOfLastLog = currentPage * logsPerPage;
  const indexOfFirstLog = indexOfLastLog - logsPerPage;
  const currentLogs = filteredLogs.slice(indexOfFirstLog, indexOfLastLog);

  return (
    <div className="page-wrapper">
      <div className="activity-log-container">
        <div className="page-head">
          <div>
            <h1 className="page-title">User Activity Log</h1>
            <p className="page-desc">
              Timeline of book changes, cover/digital/print uploads, sharing, and console actions.
            </p>
          </div>
        </div>

        <div className="panel" style={{ marginBottom: 18 }}>
          <SearchBar
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            searchType={searchType}
            setSearchType={setSearchType}
            timezone={timezone}
            setTimezone={setTimezone}
          />
        </div>

        <div className="panel">
          {loading ? (
            <div className="empty-note">Loading logs…</div>
          ) : error ? (
            <div className="empty-note al-error">{error}</div>
          ) : logs.length === 0 ? (
            <div className="empty-note">No activity logs found.</div>
          ) : filteredLogs.length === 0 ? (
            <div className="empty-note">No logs match your filters.</div>
          ) : (
            <div className="table-wrap">
              <table className="al-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>User</th>
                    <th>Action</th>
                    <th>Details</th>
                    <th>Timestamp</th>
                  </tr>
                </thead>
                <tbody>
                  {currentLogs.map((log, index) => {
                    const tone = actionTone(log.action);
                    return (
                      <tr key={log.id || `${log.created_at}-${index}`}>
                        <td>{indexOfFirstLog + index + 1}</td>
                        <td>
                          <div className="cell-title">{log.user_name || 'N/A'}</div>
                          <div className="cell-sub">{log.user_email || 'N/A'}</div>
                        </td>
                        <td>
                          <span className={`chip chip-${tone}`}>
                            {(log.action || '').replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>
                          <div className="al-details">{formatLogDetails(log.action, log.details)}</div>
                        </td>
                        <td>
                          <div className="al-timestamp">
                            {getTimezoneFormattedDate(log.created_at, timezone)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <Pagination
          currentPage={currentPage}
          totalCount={filteredLogs.length}
          pageSize={logsPerPage}
          onPageChange={(page) => setCurrentPage(page)}
        />
      </div>
    </div>
  );
}

export default ActivityLog;
